import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { STONE_TYPES, getRandomOutcome } from '../../utils/augmentData';
import { soundManager } from '../../utils/soundManager';

export default function SimulatorPanel({ active }) {
  const { t, lang } = useLanguage();
  
  const [selectedStone, setSelectedStone] = useState('TOP');
  const [isAugmenting, setIsAugmenting] = useState(false);
  const [result, setResult] = useState(null);
  const [soundsLoaded, setSoundsLoaded] = useState(false);
  const [particles, setParticles] = useState([]);
  const [history, setHistory] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isMuted, setIsMuted] = useState(() => soundManager.isMuted());
  const augmentCardRef = useRef(null);
  const particleTimeoutRef = useRef(null);
  
  // Stats tracking
  const [stats, setStats] = useState({
    totalAugments: 0,
    skillsObtained: 0,
    currentStreak: 0,
    bestStreak: 0,
    worstStreak: 0,
    lastWasSkill: false
  });

  useEffect(() => {
    soundManager.init().then(() => {
      setSoundsLoaded(true);
    });
    
    // iOS/Safari: Resume audio context when app comes back from background
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        soundManager.resumeAudio();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleToggleMute = useCallback(() => {
    const newMuted = soundManager.toggleMute();
    setIsMuted(newMuted);
  }, []);

  const createParticles = (count, isSkill = false, color = '#ffd43b') => {
    // Cancel any pending particle cleanup from a previous augment
    if (particleTimeoutRef.current) {
      clearTimeout(particleTimeoutRef.current);
    }

    const newParticles = Array.from({ length: count }, (_, i) => ({
      id: Date.now() + i,
      angle: Math.random() * Math.PI * 2,
      speed: 0.8 + Math.random() * 1.5,
      size: 2 + Math.random() * 3,
      delay: Math.random() * 1.0,
      isSkill,
      color
    }));
    setParticles(newParticles);

    particleTimeoutRef.current = setTimeout(() => {
      setParticles([]);
      particleTimeoutRef.current = null;
    }, 3500);
  };

  const handleAugment = useCallback(async () => {
    if (!soundsLoaded || isAugmenting) return;

    // Clear previous result and snap item to start position instantly
    setResult(null);
    setShowSuccess(false);
    setIsResetting(true);

    // Wait for browser to paint the reset state (item at left:20% with no transition)
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Start the augment animation from the correct start position
    setIsResetting(false);
    setIsAugmenting(true);

    await soundManager.playPlaceStone();
    
    // Start particles during merge
    setTimeout(() => {
      createParticles(64, false, STONE_TYPES[selectedStone].color);
    }, 800);
    
    // Start merging when loading sound plays
    setTimeout(async () => {
      await soundManager.playLoading();
    }, 300);

    // Show result when loading sound ends
    setTimeout(async () => {
      const outcome = getRandomOutcome(selectedStone);
      const isSkill = outcome.skill !== null;
      
      // Update stats
      setStats(prev => {
        const newTotal = prev.totalAugments + 1;
        const newSkills = prev.skillsObtained + (isSkill ? 1 : 0);
        
        let newCurrentStreak, newBestStreak, newWorstStreak;
        
        const isUltimate = selectedStone === 'ultimate';
        
        if (isSkill) {
          newCurrentStreak = prev.lastWasSkill ? prev.currentStreak + 1 : 1;
          newBestStreak = isUltimate ? prev.bestStreak : Math.max(prev.bestStreak, newCurrentStreak);
          newWorstStreak = prev.worstStreak;
        } else {
          newCurrentStreak = prev.lastWasSkill ? 1 : prev.currentStreak + 1;
          newBestStreak = prev.bestStreak;
          newWorstStreak = isUltimate ? prev.worstStreak : Math.max(prev.worstStreak, newCurrentStreak);
        }
        
        return {
          totalAugments: newTotal,
          skillsObtained: newSkills,
          currentStreak: newCurrentStreak,
          bestStreak: newBestStreak,
          worstStreak: newWorstStreak,
          lastWasSkill: isSkill
        };
      });

      // Show result after a brief moment
      setTimeout(() => {
        setResult(outcome);
        setShowSuccess(true);
        setIsAugmenting(false);
        
        soundManager.playResult(outcome.type, outcome.rarity);

        // Add to history
        setHistory(prev => [{
          stone: STONE_TYPES[selectedStone],
          outcome,
          timestamp: new Date()
        }, ...prev].slice(0, 10));
      }, 150);
    }, 2636);
  }, [soundsLoaded, isAugmenting, selectedStone]);

  // Spacebar support
  useEffect(() => {
    if (!active) return;
    
    const handleKeyDown = (e) => {
      // Only trigger on space, ignore if typing in input
      if ((e.code === 'Space' || e.key === ' ') && 
          e.target.tagName !== 'INPUT' && 
          e.target.tagName !== 'TEXTAREA' &&
          !e.target.isContentEditable) {
        e.preventDefault();
        e.stopPropagation();
        
        // Scroll augment card to top
        if (augmentCardRef.current) {
          augmentCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        // Trigger augment
        handleAugment();
      }
    };

    // Use capture phase to catch event before browser default
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [active, handleAugment]);

  const getResultColor = (result) => {
    if (!result) return '#ffd43b';
    return result.skill ? '#ff6b6b' : '#ffd43b';
  };

  const formatResult = (outcome) => {
    if (!outcome) return [];
    
    const lines = [];
    
    if (outcome.stats && outcome.stats.length > 0) {
      outcome.stats.forEach(stat => {
        lines.push(`${stat.name} ${stat.value}`);
      });
    }
    
    if (outcome.skill) {
      lines.push(outcome.skill.name);
    }
    
    return lines;
  };

  if (!active) return null;

  return (
    <div className="simulator-panel">
      {/* Augmentation Display */}
      <div className="card augment-display-card" ref={augmentCardRef}>
        <div className="augment-display">
          {/* Item and Stone Display */}
          <div className={`item-stone-container ${result && !isAugmenting ? 'centered' : ''}`}>
            {/* Item icon */}
            <div className={`item-display ${isAugmenting ? 'merging' : ''} ${result ? 'result' : ''} ${isResetting ? 'resetting' : ''}`}>
              <div className="stone-icon-large" style={{'--stone-color': '#ffffff', height: '96px', width: '96px'}}>
                <span className="stone-svg" style={{ maskImage: 'url(/icons/weapon.svg)', WebkitMaskImage: 'url(/icons/weapon.svg)' }} />
              </div>
              {result && !isAugmenting && (
                <div className="weapon-glow-effect" style={{
                  background: result.skill 
                    ? 'radial-gradient(circle, rgba(255,107,107,0.7), transparent)'
                    : 'radial-gradient(circle, rgba(255,212,59,0.6), transparent)'
                }}></div>
              )}
            </div>

            {/* Stone icon */}
            {(!result || isAugmenting) && (
              <div 
                key={selectedStone} 
                className={`stone-display ${isAugmenting ? 'merging' : ''}`}
              >
                <div
                  className="stone-icon-large"
                  style={{ '--stone-color': STONE_TYPES[selectedStone].color }}
                >
                  <span className="stone-svg" style={{ maskImage: 'url(/icons/augment.cropped.svg)', WebkitMaskImage: 'url(/icons/augment.cropped.svg)' }} />
                </div>
                {isAugmenting && (
                  <div 
                    className="stone-glow-effect" 
                    key={`glow-${selectedStone}`}
                    style={{
                      background: `radial-gradient(circle, ${STONE_TYPES[selectedStone].color}80, transparent)`
                    }}
                  ></div>
                )}
              </div>
            )}
          </div>

          {/* Particles */}
          {particles.map(particle => (
            <div
              key={particle.id}
              className="particle"
              style={{
                '--angle': `${particle.angle}rad`,
                '--speed': particle.speed,
                '--delay': `${particle.delay}s`,
                '--size': `${particle.size}px`,
                background: particle.color,
              }}
            />
          ))}

          {/* Instruction text when idle */}
          {!isAugmenting && !result && (
            <div className="instruction-text">
              {t('sim_instruction')}
            </div>
          )}

          {/* Result text at bottom */}
          <div className="result-text-area">
            {result && formatResult(result).map((line, idx) => (
              <div 
                key={idx} 
                className="result-line"
                style={{ color: getResultColor(result) }}
              >
                {line}
              </div>
            ))}
            {showSuccess && (
              <div className="success-message">
                {t('sim_success')}
              </div>
            )}
          </div>
        </div>

        {/* Stone Selector */}
        <div className="stones-selector-inline">
          {Object.entries(STONE_TYPES).map(([key, stone]) => (
            <button
              key={key}
              className={`stone-btn-inline ${selectedStone === key ? 'active' : ''}`}
              onClick={() => {
                setSelectedStone(key);
                setResult(null);
                setShowSuccess(false);
              }}
              disabled={isAugmenting}
              title={stone.name}
            >
              <span className="stone-icon-inline" style={{ '--stone-color': stone.color }}>
                <span className="stone-svg-inline" style={{ maskImage: 'url(/icons/augment.cropped.svg)', WebkitMaskImage: 'url(/icons/augment.cropped.svg)' }} />
              </span>
              <span className="stone-name-inline">
                {stone.name.replace(' Life Stone', '')}
              </span>
            </button>
          ))}
        </div>

        {/* Augment Button Row */}
        <div className="augment-btn-row">
          <button 
            className={`augment-btn ${isAugmenting ? 'disabled' : ''}`}
            onClick={handleAugment}
            disabled={!soundsLoaded || isAugmenting}
          >
            {!soundsLoaded ? t('sim_loading') : 
             isAugmenting ? t('sim_augmenting') : 
             t('sim_augment_btn')}
          </button>
          <span
            className={`mute-btn-icon ${isMuted ? 'muted' : ''}`}
            onClick={handleToggleMute}
            title={isMuted ? t('sim_unmute') : t('sim_mute')}
            role="button"
            tabIndex={0}
            style={{ maskImage: 'url(/icons/sound-2.cropped.svg)', WebkitMaskImage: 'url(/icons/sound-2.cropped.svg)' }}
          />
        </div>
        
        {/* Stats Display */}
        {stats.totalAugments > 0 && (
          <div className="stats-display">
            <div className="stat-item">
              <span className="stat-label">{t('sim_used')}:</span>
              <span className="stat-value">{stats.totalAugments}</span>
            </div>
            <div className="stat-separator">•</div>
            <div className="stat-item">
              <span className="stat-label">{t('sim_skill_rate')}:</span>
              <span className="stat-value">
                {((stats.skillsObtained / stats.totalAugments) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="stat-separator">•</div>
            <div className="stat-item">
              <span className="stat-label">{t('sim_best')}:</span>
              <span className="stat-value skill-stat">{stats.bestStreak}</span>
            </div>
            <div className="stat-separator">•</div>
            <div className="stat-item">
              <span className="stat-label">{t('sim_worst')}:</span>
              <span className="stat-value no-skill-stat">{stats.worstStreak}</span>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="card history" key={`history-${lang}`}>
          <div className="card-title">{t('sim_history')}</div>
          <div className="history-list">
            {history.map((entry, index) => (
              <div key={index} className="history-item">
                <div className="history-header">
                  <span className="history-stone" style={{ '--stone-color': entry.stone.color }}>
                    <span className="stone-svg-history" style={{ maskImage: 'url(/icons/augment.cropped.svg)', WebkitMaskImage: 'url(/icons/augment.cropped.svg)' }} />
                  </span>
                  <span className="history-stone-name">
                    {entry.stone.name.replace(' Life Stone', '')}
                  </span>
                </div>
                <div
                  className="history-result"
                  style={{
                    color: entry.outcome.skill ? '#ff6b6b' : '#ffd43b'
                  }}
                >
                  {entry.outcome.skill && (
                    <span>{entry.outcome.skill.name}</span>
                  )}
                  {entry.outcome.stats && entry.outcome.stats.length > 0 && (
                    <span>
                      {entry.outcome.skill && ', '}
                      {entry.outcome.stats.map((stat, i) => (
                        <span key={i}>
                          {i > 0 && ', '}
                          {stat.name} {stat.value}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
